import { useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import { fetchJobCategoryData } from '../_api';
import { fetchJobCategoryData as fetchJobCategoryDataEpicHP } from 'pages/ApplicationRequest/components/containers/1696EpicHP/_api';
import { nanoid } from 'nanoid';
import { debounceAsync } from 'utils/misc/debounce';
import { useDispatch } from 'react-redux';
import { clearMessages } from 'store/ancillary/actions';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import { EPIC_APP_ID, NO_ACCESS_OR_TRAINING_NEEDED_JOB_CATEGORIES } from '../_const';
import { HP_APP_ID } from 'pages/ApplicationRequest/components/containers/1696EpicHP/_const';

const JobCategorySearch = ({
	value,
	allValues,
	setValue = () => {},
	onBlur = () => {},
	onHelp = () => {},
	touched,
	error,
	helperText,
	customFilter = () => true,
	autoFocus = true,
	readOnly = false,
	systemID = EPIC_APP_ID,
	...props
}) => {
	const [sLoading, setLoading] = useState(false);
	const [sHasSearched, setHasSearched] = useState(false);
	const [sOpen, setOpen] = useState(false);
	const [options, setOptions] = useState([]);
	const dispatch = useDispatch();

	/**
	 * Memoized function required in order to preserve timeout IDs, otherwise function will be created new each render.
	 */
	const debouncedSearch = useMemo(() => {
		return debounceAsync(async input => {
			try {
				const processedInput = String(input).trim();
				let fetchResults = {};
				switch (systemID) {
					case HP_APP_ID:
						if (processedInput.length > 0) {
							fetchResults = await fetchJobCategoryDataEpicHP(
								processedInput,
								dispatch,
							);
						}
						break;
					default:
						fetchResults = await fetchJobCategoryData(
							processedInput,
							dispatch,
						);
						break;
				}
				let optionsList = [];
				if (typeof fetchResults === 'object') {
					const options = Object.keys(fetchResults)
						.map(key => ({
							key: key,
							value: fetchResults[key],
						}))
						.filter(
							jc =>
								!NO_ACCESS_OR_TRAINING_NEEDED_JOB_CATEGORIES.some(
									noAccess => {
										return (
											jc.value?.jobCategoryId ===
												noAccess.id ||
											String(
												jc.value?.jobCategoryName ?? '',
											)
												.toLowerCase()
												.trim() ===
												noAccess.name.toLowerCase()
										);
									},
								),
						)
						.sort((a, b) => {
							if (a.key < b.key) return -1;
							if (a.key > b.key) return 1;
							return 0;
						});
					optionsList =
						options.length > 200 ? options.slice(0, 200) : options;
				}
				const existingOptionsList = allValues.map(v => v.jobCategoryId);
				const processedOptionsList = optionsList.filter(
					o => !existingOptionsList.includes(o.value.jobCategoryId),
				);
				const customFilteredProcessedOptionsList =
					processedOptionsList.filter(customFilter);
				setOptions(customFilteredProcessedOptionsList);
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			} finally {
				setLoading(false);
			}
		});
	}, [dispatch, allValues, customFilter, systemID]);

	return (
		<FormControl variant='filled' fullWidth>
			<Autocomplete
				autoComplete
				clearIcon={null}
				disableClearable
				disabled={readOnly}
				filterOptions={options => options}
				forcePopupIcon={!readOnly}
				groupBy={option => option.category}
				getOptionLabel={option => (option.key ? option.key : option)}
				isOptionEqualToValue={(option, value) => {
					return option.key
						? option.key === value || option.value === value
						: option === value;
				}}
				open={readOnly ? false : sOpen}
				onOpen={() => {
					if (!readOnly) {
						setOpen(true);
					}
				}}
				onClose={() => setOpen(false)}
				readOnly={readOnly}
				loading={sLoading}
				noOptionsText={sHasSearched ? 'No results' : 'Type to search'}
				onChange={(_, newValue) => {
					if (readOnly) {
						return;
					}

					if (value && newValue?.value) {
						setValue({
							...newValue?.value,
							jobCategoryExperience: value.jobCategoryExperience,
							wave: value.wave,
						});
					} else if (newValue?.value) {
						setValue(newValue.value);
					} else {
						setValue({
							jobCategoryId: nanoid(),
							jobCategoryName: null,
							jobCategoryType: null,
							jobCategoryExperience: 'New',
							active: true,
							wave: null,
						});
					}
				}}
				onInputChange={(_, newInputValue) => {
					if (readOnly) {
						return;
					}

					setHasSearched(!!newInputValue);
					setLoading(true);
					debouncedSearch(newInputValue);
				}}
				options={options}
				renderInput={params => (
					<TextField
						{...params}
						autoFocus={autoFocus}
						fullWidth
						InputLabelProps={{ shrink: false }}
						InputProps={{
							...params.InputProps,
							readOnly,
							endAdornment: (
								<>
									<InputAdornment
										sx={{
											marginBottom: '8px',
										}}
										position='end'
									>
										{sLoading ? (
											<CircularProgress
												size={20}
												variant='indeterminate'
											/>
										) : value?.jobCategoryName &&
										  !readOnly ? (
											<Tooltip
												title='Click for information on this job category.'
												placement='top'
											>
												<IconButton
													sx={{ padding: '0' }}
													aria-label='Help'
													edge='end'
													size='small'
													onClick={onHelp}
												>
													<InfoIcon fontSize='small' />
												</IconButton>
											</Tooltip>
										) : null}
									</InputAdornment>
									{params?.InputProps?.endAdornment}
								</>
							),
							placeholder: 'Type to search for Job Categories',
						}}
						onBlur={e => {
							if (value?.jobCategoryName) {
								setOptions([
									{
										key: value.jobCategoryName,
										value: value,
									},
								]);
							}
							onBlur(e);
						}}
						name={props.name}
						size='small'
						variant='standard'
						{...props}
					/>
				)}
				renderOption={(props, option, { inputValue }) => {
					const optionStr = option.key ? option.key : option;
					const matches = match(optionStr, inputValue, {
						insideWords: true,
						findAllOccurrences: true,
					});
					const parts = parse(optionStr, matches);

					return (
						<li {...props}>
							<div>
								{parts.map((part, index) => (
									<span
										key={index}
										style={{
											fontWeight: part.highlight
												? 700
												: 400,
										}}
									>
										{part.text}
									</span>
								))}
							</div>
						</li>
					);
				}}
				value={value.jobCategoryName}
			/>
			{((touched && error) || value.jobCategoryType) && (
				<FormHelperText error={!!touched && !!error}>
					{touched && error ? error : value.jobCategoryType}
				</FormHelperText>
			)}
		</FormControl>
	);
};
export default JobCategorySearch;
